import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
import { useLaunch } from '../contexts/LaunchContext';
import { StoreBadges } from './StoreBadges';
import { PhoneFrame } from './PhoneFrame';
import { screens } from '../data/screens';
import { WEB_JOIN_URL } from '../data/launch';

/** Playful reminders of what actually goes in a kitty. */
const floatingExpenses = [{
  emoji: '🍺',
  label: 'Drinks',
  amount: '£46.74',
  position: 'left-[-6%] top-[16%]'
}, {
  emoji: '🚕',
  label: 'Taxi',
  amount: '£28.50',
  position: 'left-[-2%] bottom-[16%]'
}, {
  emoji: '🏠',
  label: 'Accommodation',
  amount: '£584.65',
  position: 'right-[-4%] top-[9%]'
}, {
  emoji: '🎟️',
  label: 'Tickets',
  amount: '£125.74',
  position: 'right-[-6%] bottom-[22%]'
}];
export function HeroSection() {
  const {
    isPublicLaunch,
    googlePlayUrl
  } = useLaunch();
  const reduceMotion = useReducedMotion();
  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({
    behavior: 'smooth'
  });
  return <section className="relative overflow-hidden pt-32 pb-16 sm:pt-36 lg:pt-40 lg:pb-24" data-id="element-161" data-mp="2bdx4wovspnnr">
      {/* Warm wash behind the composition */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10" data-id="element-162" data-mp="2p6d93t0fnrq9">
        <div className="absolute -right-[10%] -top-[15%] h-[65%] w-[60%] rounded-full bg-kitty-yellow-100/50 blur-3xl" data-id="element-163" data-mp="2ked3bjxom4lr" />
      </div>

      <div className="mx-auto max-w-7xl px-6 sm:px-8" data-id="element-164" data-mp="2z1y6teygy5u7">
        <div className="grid items-center gap-14 lg:grid-cols-12 lg:gap-8" data-id="element-165" data-mp="2u4xz6zlz0v0z">
          {/* Copy */}
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.55,
          ease: [0.22, 1, 0.36, 1]
        }} className="lg:col-span-5" data-id="element-166" data-mp="384pjfi32z7nf">
            {isPublicLaunch && <p className="mb-6 inline-flex items-center gap-2.5 rounded-full border border-gray-200 bg-white px-4 py-1.5 text-sm font-bold text-kitty-dark shadow-sm" data-id="element-167" data-mp="1drznyvx2dqdx">
                <span className="relative flex h-2.5 w-2.5" data-id="element-168" data-mp="38v5uv4nkxwrj">
                  <span aria-hidden="true" className="absolute inline-flex h-full w-full animate-ping rounded-full bg-kitty-green opacity-75" data-id="element-169" data-mp="3drmjdzyoyd55" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-kitty-green" data-id="element-170" data-mp="3iq7mq898pcxt" />
                </span>
                Out now
              </p>}

            <h1 className="mb-6 text-[2.5rem] font-black leading-[1.05] tracking-tight text-kitty-dark sm:text-5xl xl:text-[3.75rem]" data-id="element-171" data-mp="3rr5qcpfrnifv">
              Split the cost.
              <br data-id="element-172" data-mp="5cmf31m38jdm" />
              Not the friendship.
            </h1>

            <p className="mb-7 max-w-lg text-lg leading-relaxed text-gray-600 lg:text-xl" data-id="element-173" data-mp="5epc45lnrcru">
              Track group expenses, see exactly who owes what and settle up without the awkwardness. Friends can even join from their browser, no app required.
            </p>

            <div className="mb-6" data-id="element-174" data-mp="jdf5pb99qubg">
              <a href={WEB_JOIN_URL} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[56px] items-center justify-center gap-2 rounded-full border-2 border-kitty-dark bg-white px-8 text-base font-bold text-kitty-dark transition-colors duration-150 hover:bg-gray-50 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-dark focus-visible:ring-offset-2" data-id="element-175" data-mp="jaazk8c8toi5">
                Been invited? Join here
                <ArrowRightIcon aria-hidden="true" className="h-5 w-5" data-id="element-176" data-mp="2ml5v87t4r71h" />
              </a>
            </div>

            <p className="mb-4 text-base font-bold text-kitty-dark" data-id="element-177" data-mp="2ikdktb8iuw3m">
              Free to download. Free to use. No subscription.
            </p>

            <StoreBadges tone="light" data-id="element-178" data-mp="2dq9aqj3ym7yb" />
          </motion.div>

          {/* Product composition */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.1,
          ease: [0.22, 1, 0.36, 1]
        }} className="lg:col-span-7" data-id="element-179" data-mp="28rok9beif2n2">
            <div className="relative mx-auto w-full max-w-[300px] sm:max-w-[620px]" data-id="element-180" data-mp="2l4s2440gidpk">
              {/* Behind, left: My Balance */}
              <PhoneFrame screen={screens.myBalance} decorative className="absolute left-0 top-[10%] hidden w-[42%] -rotate-[7deg] sm:block" data-id="element-181" data-mp="2c292dsrywh7j" />

              {/* Behind, right: settlement history */}
              <PhoneFrame screen={screens.history} decorative className="absolute right-0 top-[16%] hidden w-[42%] rotate-[7deg] sm:block" data-id="element-182" data-mp="34qwua77824u2" />

              {/* Front and centre: the kitty itself */}
              <div className="relative z-20 mx-auto w-full sm:w-[52%]" data-id="element-183" data-mp="2v13eeu01r3k0">
                <PhoneFrame screen={screens.kittyMain} shadow="xl" data-id="element-184" data-mp="2uz0t2n9u9ylm" />
              </div>

              {/* Floating expenses */}
              {floatingExpenses.map((expense, index) => <motion.div key={expense.label} aria-hidden="true" initial={{
              opacity: 0,
              y: 10
            }} animate={reduceMotion ? {
              opacity: 1,
              y: 0
            } : {
              opacity: 1,
              y: [0, -7, 0]
            }} transition={reduceMotion ? {
              duration: 0.3
            } : {
              opacity: {
                duration: 0.4,
                delay: 0.4 + index * 0.1
              },
              y: {
                duration: 4 + index * 0.5,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: index * 0.3
              }
            }} className={`absolute z-30 hidden items-center gap-2.5 rounded-2xl border border-gray-100 bg-white px-3.5 py-2.5 shadow-card lg:flex ${expense.position}`} data-id="element-185" data-mp="3nny0fvciiazl" data-mpi={index}>
                  <span className="text-lg leading-none" data-id="element-186" data-mp="3nv9oo9ddn4f7">{expense.emoji}</span>
                  <span className="text-sm font-bold text-kitty-dark" data-id="element-187" data-mp="3nxcotm88udxn">
                    {expense.label}
                  </span>
                  <span className="text-sm font-black text-kitty-dark" data-id="element-188" data-mp="135atz1lceruj">
                    {expense.amount}
                  </span>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}